import { useEffect, useId, useState, useMemo, type ChangeEvent, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ITEM_TYPES, type ItemType, type TemplateItem } from '@pius/contracts';
import { parseItemImport, generateItemsCsvSample, type ParsedItem } from './bulkItemParser';
import { downloadFile } from './templateBundle';
import './template-modals.css';

interface BulkItemModalProps {
  sectionLabel: string;
  existingItems: TemplateItem[];
  isOpen: boolean;
  onClose: () => void;
  onApply: (items: TemplateItem[]) => void;
}

export function BulkItemModal({
  sectionLabel,
  existingItems,
  isOpen,
  onClose,
  onApply,
}: BulkItemModalProps): React.JSX.Element | null {
  const { t } = useTranslation('common');
  const fileInputId = useId();

  const [rawText, setRawText] = useState('');
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
  const [editedItems, setEditedItems] = useState<ParsedItem[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClose = () => {
    setRawText('');
    setImportMode('append');
    setEditedItems(null);
    setIsDragging(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const existingKeys = useMemo(
    () => new Set(importMode === 'append' ? existingItems.map((i) => i.key) : []),
    [existingItems, importMode],
  );

  const parsedResult = useMemo(() => {
    return parseItemImport(rawText, existingKeys);
  }, [rawText, existingKeys]);

  const activeItems = editedItems ?? parsedResult.items;
  const validItems = activeItems.filter((i) => i.isValid);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        setRawText(content);
        setEditedItems(null);
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const updateRow = (index: number, patch: Partial<ParsedItem>) => {
    const next = [...activeItems];
    const curr = next[index];
    if (curr) {
      next[index] = { ...curr, ...patch };
      setEditedItems(next);
    }
  };

  const removeRow = (index: number) => {
    const next = activeItems.filter((_, i) => i !== index);
    setEditedItems(next);
  };

  const handleDownloadSample = () => {
    const sample = generateItemsCsvSample();
    downloadFile('section-items-sample.csv', sample, 'text/csv');
  };

  const handleApply = () => {
    const formatted: TemplateItem[] = validItems.map((item, idx) => {
      const baseOrdinal = importMode === 'append' ? existingItems.length : 0;
      return {
        key: item.key,
        label: item.label,
        item_type: item.item_type,
        ordinal: baseOrdinal + idx + 1,
        options: [],
      };
    });

    const finalItems = importMode === 'append' ? [...existingItems, ...formatted] : formatted;

    onApply(finalItems);
    handleClose();
  };

  return createPortal(
    <div
      className="tpl-modal__overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="tpl-modal tpl-modal--large">
        <header className="tpl-modal__head">
          <div>
            <p className="tpl-eyebrow">{t('templates.bulk_items_importer_eyebrow')}</p>
            <h2 className="tpl-modal__title">
              {t('templates.bulk_items_importer_title', { section: sectionLabel })}
            </h2>
          </div>
          <button
            type="button"
            className="tpl-modal__close-btn"
            onClick={handleClose}
            aria-label={t('action.close', 'Close')}
          >
            ✕
          </button>
        </header>

        <p className="tpl-modal__lede">{t('templates.bulk_items_importer_lede')}</p>

        <div className="tpl-modal__body">
          <div className="tpl-bulk__controls">
            <div className="tpl-bulk__upload-row">
              <label
                htmlFor={fileInputId}
                className={`tpl-bulk__dropzone ${isDragging ? 'tpl-bulk__dropzone--active' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <span>{t('templates.bulk_items_dropzone')}</span>
              </label>
              <input
                id={fileInputId}
                type="file"
                accept=".csv,.tsv,.txt"
                className="tpl-bulk__file-input"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              <button
                type="button"
                className="tpl-btn tpl-btn--ghost"
                onClick={handleDownloadSample}
              >
                📥 {t('templates.bulk_download_sample')}
              </button>
            </div>

            <label className="tpl-modal__field">
              <span className="tpl-modal__field-label">
                {t('templates.bulk_items_paste_label')}
                {rawText && (
                  <span className="tpl-bulk__format-badge">
                    {t(`templates.detected_${parsedResult.delimiterDetected}`)}
                    {parsedResult.hasHeader ? ` · ${t('templates.header_detected')}` : ''}
                  </span>
                )}
              </span>
              <textarea
                className="tpl-bulk__textarea tpl-modal__mono"
                rows={6}
                value={rawText}
                placeholder={`key,label,type\nfirst_reading,"First Reading",scripture_ref\nresponsorial_psalm,"Responsorial Psalm",scripture_ref\ngospel,"Gospel",scripture_ref\nopening_song,"Opening Hymn",music_ref\n\n-- or simply paste item names one per line --\nFirst Reading\nResponsorial Psalm\nGospel Reading\nEntrance Hymn\nLighting of Unity Candle`}
                onChange={(e) => {
                  setRawText(e.target.value);
                  setEditedItems(null);
                }}
              />
            </label>

            {activeItems.length > 0 && (
              <div className="tpl-bulk__mode-select">
                <span className="tpl-bulk__mode-label">{t('templates.import_mode_label')}:</span>
                <label className="tpl-bulk__radio">
                  <input
                    type="radio"
                    name="importMode"
                    value="append"
                    checked={importMode === 'append'}
                    onChange={() => setImportMode('append')}
                  />
                  <span>
                    {t('templates.import_mode_append_items', { count: existingItems.length })}
                  </span>
                </label>
                <label className="tpl-bulk__radio">
                  <input
                    type="radio"
                    name="importMode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                  />
                  <span>{t('templates.import_mode_replace_items')}</span>
                </label>
              </div>
            )}
          </div>

          {activeItems.length > 0 && (
            <div className="tpl-bulk__preview-box">
              <div className="tpl-bulk__preview-head">
                <span className="tpl-bulk__preview-title">
                  {t('templates.preview_title')} ({validItems.length} {t('templates.valid_items')})
                </span>
                <span className="tpl-bulk__preview-hint">{t('templates.preview_edit_hint')}</span>
              </div>

              <div className="tpl-bulk__table-wrapper">
                <table className="tpl-bulk__table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th style={{ width: '220px' }}>{t('templates.col_key')}</th>
                      <th>{t('templates.col_label')}</th>
                      <th style={{ width: '180px' }}>{t('templates.col_type')}</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeItems.map((item, idx) => (
                      <tr key={idx} className={!item.isValid ? 'tpl-bulk__row--invalid' : ''}>
                        <td className="tpl-bulk__cell-num">{idx + 1}</td>
                        <td>
                          <input
                            className="tpl-modal__mono tpl-bulk__cell-input"
                            value={item.key}
                            onChange={(e) => updateRow(idx, { key: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="tpl-bulk__cell-input"
                            value={item.label}
                            onChange={(e) => updateRow(idx, { label: e.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            className="tpl-bulk__cell-input"
                            value={item.item_type}
                            onChange={(e) =>
                              updateRow(idx, { item_type: e.target.value as ItemType })
                            }
                          >
                            {ITEM_TYPES.map((tVal) => (
                              <option key={tVal} value={tVal}>
                                {t(`templates.type_${tVal}`, {
                                  defaultValue: tVal.replace(/_/g, ' '),
                                })}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="tpl-btn tpl-btn--ghost"
                            onClick={() => removeRow(idx)}
                            title={t('action.remove')}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <footer className="tpl-modal__foot">
          <button type="button" className="tpl-btn tpl-btn--ghost" onClick={handleClose}>
            {t('action.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="tpl-btn tpl-btn--primary"
            disabled={validItems.length === 0}
            onClick={handleApply}
          >
            {importMode === 'append'
              ? t('templates.btn_add_count_items', { count: validItems.length })
              : t('templates.btn_replace_count_items', { count: validItems.length })}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
